/** Testing the proposed rules package before anybody activates it — **REP-08**.
 *
 * The acceptance clause is *"a taxonomy change is tested before activating a new reporting
 * package"*. `packages.require_static` already gates `load_regimes`, which is the real activation
 * path, and `GET …/package-preflight` has been the data-backed other half since it was built: the
 * test a person runs against **real figures** before deciding. It had no screen, so the only way to
 * run it was a Python session — which means the acceptance clause was satisfied by a thing nobody
 * in the product could do.
 *
 * ## Where this lives, and why it is here rather than on Administration
 *
 * On **Reports**, last, full width. Three reasons, and the first is the one that decides it:
 *
 * 1. **The route is obligation-scoped** (ADR-0029: resolve, then authorize, then describe). The
 *    entity, the period, the boundary and the regime all come from the obligation, and `/reports`
 *    is the only screen that already resolves one. Putting this on Administration would mean a
 *    second obligation picker, and two pickers for one fact are two pickers that eventually
 *    disagree — which is finding F02 with a different face.
 * 2. **It is the same reader asking the next question.** *"What this regime requires stated"* and
 *    *"what was stated"* are already adjacent here, which is the adjacency `CLAUDE.md` describes
 *    for *Filings issued beside the preview*; *"what would change in what we claim the law says"*
 *    is the third of the set.
 * 3. **It is not a step in the close.** Reconcile, close, issue is a sequence a person works
 *    through, and this is a change **nobody has made yet** — so it sits below that sequence rather
 *    than inside it, beside *Against last year*, which is the panel's other neighbour that is
 *    EmitIQ's own analysis rather than part of a filing.
 *
 * A separate route was the alternative and was rejected on reason 1 alone: it would have had to
 * carry an obligation in its URL, and an obligation in a URL that then decides what a reader may
 * see is the authorization-from-attacker-input shape ADR-0029 exists to prevent.
 *
 * ## Four things this screen has to get right
 *
 * - **`recited` is its own kind, and it is the loudest thing on the panel.** A changed *citation*
 *   under an unchanged sentence is the edit a reviewer skims past, and it is the one that moves
 *   what the product claims the law says. So it is not a row in a list of changes with a different
 *   word in it: it is the **first** group, in its own bordered block, with its own sentence saying
 *   why it matters, and the old and new citation set out as a pair. Rendering it as a flavour of
 *   `amended` is the change that undoes this, and `packagePreflight.test.tsx` fails if it does.
 * - **`passed` is not approval, and `activation_is_not_here` says why.** That string is on every
 *   response deliberately, because the one place somebody will look for it is the thing they have
 *   just run. It is rendered directly under the verdict, not in a footnote, and the verdict's own
 *   headline is named after what *was done* — the rule F07 left behind.
 * - **`empty_lines` and `not_exercised` are two facts and never one list.** The first is *"this
 *   line was expected to produce a figure and did not"*, which is not automatically a failure: a
 *   company with no gas genuinely has no gas line. The second is *"this trial could not exercise
 *   this line at all"*, which is true of SECR's 20H comparatives because a trial report has no
 *   legal entity to look a prior disclosure up against. Merging them would report six problems
 *   with a package that has none; dropping the second would let a reader believe the package was
 *   fully tested. Two panels, two headings, two sentences, two counts.
 * - **The sentence is the server's.** `Preflight.sentence` never says "no changes" when nothing was
 *   compared — a regime whose proposed version is not loaded has nothing to diff against, and
 *   `from_version` is `null` there. So it is rendered verbatim and never composed from the counts
 *   on this side, which is the same rule design principle 7 states for every sentence that appears
 *   on two screens.
 */

import { useState } from "react";

import { ReadFailed } from "../components/Loaded";
import { Empty, Panel } from "../components/Panel";
import { useMe } from "../api/auth";
import { usePackagePreflight } from "../api/queries";
// The ordering and the no-loss rule live in their own module, like `search.ts`'s URL contract: they
// are a decision rather than a layout, and `packagePreflight.test.tsx` asserts them without a DOM.
import { groupChanges } from "./preflight";
import styles from "./Screen.module.css";
import own from "./PackagePreflight.module.css";

/** One list of disclosure keys, with the sentence that says what the list means.
 *
 * Shared by the two lists below **and taking its sentence as a prop**, which is the opposite of
 * what a shared component usually does with copy. The two facts look identical — a heading and
 * some keys — and mean nearly opposite things, so the sentence is the only part a reader can tell
 * them apart by. A default would be the merge this panel exists to refuse, one layer down.
 */
function KeyList({
  testId,
  heading,
  sentence,
  keys,
}: {
  testId: string;
  heading: string;
  sentence: string;
  keys: readonly string[];
}) {
  if (keys.length === 0) return null;
  return (
    <div className={own.keyBlock} data-testid={testId}>
      <span className={own.keyHeading}>
        {heading} <span className={own.keyCount}>{keys.length}</span>
      </span>
      <span className={own.keyWhy}>{sentence}</span>
      <ul className={own.keys}>
        {keys.map((key) => (
          <li key={key}>{key}</li>
        ))}
      </ul>
    </div>
  );
}

/** What this panel says before anybody has run anything.
 *
 * Not a blank panel and not an auto-fetch. A reader who has never run a preflight needs to know the
 * thing exists and what pressing it does — including, before they press it, that it writes nothing.
 */
function Invitation({ onRun, entityName }: { onRun: () => void; entityName: string }) {
  return (
    <>
      <p className={styles.note}>
        This builds every disclosure the <strong>source tree&rsquo;s</strong> package defines
        against {entityName}&rsquo;s own figures, in memory, and compares it with the package that
        is loaded. It <strong>writes nothing</strong>, and it activates nothing.
      </p>
      <p className={own.runRow}>
        <button type="button" className={styles.primary} data-testid="preflight-run" onClick={onRun}>
          Test the proposed package
        </button>
      </p>
    </>
  );
}

export function PackagePreflightPanel({
  obligationId,
  entityName,
}: {
  obligationId: string;
  /** The filing's own entity, as `Filings` names it. The invitation says whose figures the trial
   * is built against, and a regime key would not: a group has two entities filing under SECR. */
  entityName: string;
}) {
  const { data: me } = useMe();
  // Approver and Admin, matching `MAY_ISSUE` on the route. A Contributor is told whose job this is
  // rather than shown a control that predictably 403s (design rule 6, UX-04) — and the request is
  // never sent either, because a 403 the client could have predicted is the same defect one layer
  // down.
  const mayRun = me?.role === "approver" || me?.role === "admin";
  /** Whether this reader has run it. **`useState` rather than a URL parameter**, which is the
   * opposite of the rule the register's filters follow (EXP-12) and is deliberate.
   *
   * A filter is *a view somebody shares*. A preflight is not: the result is the deployed source
   * tree diffed against today's figures, so the same link run tomorrow, or on a deployment one
   * commit older, answers something else — and a link that promises a result it cannot reproduce
   * is worse than no link. The thing worth sharing is the **sentence and the diff**, which is text.
   *
   * It also keeps `validateSearch` honest. That function is the URL contract and — measured, not
   * assumed (EXP-39) — **not** the stripper its docstring once claimed, because every screen reads
   * `useSearch({ strict: false })`. So a parameter added to a control and forgotten there fails
   * nothing at all, and the only real guard is a screen test that drives the control. Not adding
   * one is the version of that with nothing to forget. */
  const [ran, setRan] = useState(false);
  const preflight = usePackagePreflight(obligationId, mayRun && ran);
  const result = preflight.data;

  return (
    <Panel
      title="Testing the proposed rules package"
      sub="Nothing here activates anything"
      column="1 / 13"
    >
      {!mayRun ? (
        /* Said, never left blank. `Transition` rendered nothing for a role that could not act, and
           nothing is indistinguishable from the feature not existing. */
        <p className={styles.note} data-testid="preflight-who-can">
          An <strong>Approver</strong> or an <strong>Admin</strong> tests a proposed package. It is
          a change to what this product states the law requires, so it is read by somebody who signs
          filings rather than by everybody who records figures.
        </p>
      ) : !ran ? (
        <Invitation onRun={() => setRan(true)} entityName={entityName} />
      ) : preflight.error ? (
        /* A failed read is not a package with nothing wrong with it. `data ?? []` here would print
           "no changes" off a dropped request, on the panel somebody reads before changing what the
           product claims a regulation says (EXP-29). */
        <ReadFailed
          of="The proposed package"
          error={preflight.error}
          onRetry={() => void preflight.refetch()}
        />
      ) : preflight.isPending || !result ? (
        <Empty>Building every proposed disclosure against this filing&rsquo;s figures…</Empty>
      ) : (
        <>
          {/* **The verdict, named after what was done.** "Passed" is not approval and not a
              statement that this package may be activated — the sentence under it says who does
              that and where. F07's rule, on a different ladder. */}
          {/* `role="status"`, the pattern `issue-replayed` already uses: pressing the button is
              the only way this content appears, so the outcome of a keyboard press should be
              announced rather than left for somebody to go and find. Said honestly: a live region
              inserted at the same moment as its content is not reliably announced by every screen
              reader, so this is an improvement and not a guarantee — the WCAG/assistive-technology
              audit is a tracked gap (32-information-architecture.md) rather than something this
              attribute closes. */}
          <div
            className={`${own.verdict} ${result.passed ? own.verdictPassed : own.verdictFailed}`}
            role="status"
            data-testid="preflight-verdict"
          >
            {result.passed
              ? "Nothing in this package would be refused at load"
              : "This package would be refused at load as it stands"}
            <span className={own.verdictSub} data-testid="preflight-sentence">
              {/* **The server's sentence, verbatim.** It never says "no changes" when nothing was
                  compared, and composing one here from `changes.length` would say exactly that for
                  a regime whose proposed version is not loaded at all. */}
              {result.sentence}
            </span>
          </div>

          <p className={styles.caveat} data-testid="preflight-activation">
            <span className={styles.caveatTitle}>This is a test, not an activation</span>
            {result.activation_is_not_here}
          </p>

          {result.findings.length > 0 ? (
            <div className={own.findings} data-testid="preflight-findings">
              {result.findings.map((finding) => (
                <div className={own.finding} key={`${finding.key}:${finding.message}`}>
                  <span
                    className={`${own.mark} ${
                      finding.severity === "blocking" ? own.markBlocking : own.markWarning
                    }`}
                    aria-hidden="true"
                  />
                  <span>
                    {/* Colour carries emphasis and never meaning on its own (design rule 8), so
                        the severity is a word as well as a dot. */}
                    <strong className={own.findingKey}>{finding.key}</strong>{" "}
                    <span className={own.severity}>{finding.severity}</span>
                    <span className={own.findingWhat}>{finding.message}</span>
                  </span>
                </div>
              ))}
            </div>
          ) : null}

          {result.changes.length === 0 ? (
            /* No invented sentence here either: `result.sentence` above has already said whether
               this means "identical" or "nothing to compare against", and those are different
               facts. This line says only what this list is. */
            <p className={styles.note}>
              No line and no check differs between the two packages.
            </p>
          ) : (
            groupChanges(result.changes).map((group) => (
              <div
                className={`${own.group} ${group.kind === "recited" ? own.groupRecited : ""}`}
                key={group.kind}
                data-testid={`preflight-${group.kind}`}
              >
                <span className={own.groupHeading}>
                  {group.heading} <span className={own.groupCount}>{group.changes.length}</span>
                </span>
                {group.why ? <span className={own.groupWhy}>{group.why}</span> : null}
                <ul className={own.changes}>
                  {group.changes.map((change) => (
                    <li key={`${change.kind}:${change.key}`} className={own.change}>
                      <span className={own.changeKey}>{change.key}</span>
                      <span className={own.changeDetail}>{change.detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}

          {/* **Two lists, and they must never become one.** The headings and the sentences are
              what tell them apart; the counts are separate for the same reason. */}
          <KeyList
            testId="preflight-empty-lines"
            heading="Produced no figure against your data"
            sentence={
              "These lines were expected to produce a figure and did not. That is not automatically " +
              "a failure — a company with no gas genuinely has no gas line — so read each one and " +
              "say which it is. A line this regime does not require of this entity is not here."
            }
            keys={result.empty_lines}
          />
          <KeyList
            testId="preflight-not-exercised"
            heading="This trial could not test these lines at all"
            sentence={
              "Not lines that came back empty: lines nothing looked up. A comparative is resolved " +
              "against what the company disclosed for the preceding year, which is per legal " +
              "entity, and a trial report has no entity — so the lookup never ran. They are named " +
              "rather than counted as problems, because a preflight that silently skipped them " +
              "would read as a preflight that passed them."
            }
            keys={result.not_exercised}
          />

          <p className={own.runRow}>
            <button
              type="button"
              className={styles.fixLink}
              data-testid="preflight-again"
              onClick={() => void preflight.refetch()}
              disabled={preflight.isFetching}
            >
              {preflight.isFetching ? "Testing…" : "Test it again"}
            </button>
          </p>
        </>
      )}
    </Panel>
  );
}
